import { DayOfWeek, LocalDate, LocalTime } from "@js-joda/core";
import React from "react";
import {
  CaregiverVisitBroadcastEngagementDetails,
  PatientTimeFrames,
  PublicVisitBroadcastDetailsTypes,
  VisitBroadcastId,
} from "../messages";
import { assertDefined } from "../utils/assertUndefinedable";
import {
  calculateDaysLeftInWeek,
  calculateHoursLeftInWeek,
  canSelectDuration,
  fixStartTimeOnDurationChange,
  getAreDaysSelectable,
  getAreTimesFlexible,
  getDaysInWeek,
  getDaysMap,
  getDisabledFlexibleDays,
  getFlexibleVisitDayCalculatedState,
  getHoursInWeek,
  getHoursPerDayMap,
  getIsDayDisabled,
  getIsSelectedDaysTimesInAvailabilityRange,
  getStartAndEndTimeMinutes,
  isDurationDisabled,
  isFlexibleVisitValid,
  localTimeToMinutes,
  updateFlexibleDaysTimes,
} from "../utils/flexibleVisitUtils";
import { HashMap } from "../utils/HashMap";

interface Params {
  visit: {
    type: "Available";
    id: VisitBroadcastId;
    publicVisitBroadcastDetails: PublicVisitBroadcastDetailsTypes.FlexiblePattern;
    caregiverVisitEngagementDetails: CaregiverVisitBroadcastEngagementDetails;
  };
}

export interface FlexibleVisitDuration {
  numberOfHours: number;
  isSelected: boolean;
  isDisabled: boolean;
}

export interface FlexibleVisitDayState {
  isChecked: boolean | null;
  isDisabled: boolean;
  durations: FlexibleVisitDuration[];
  startTime: LocalTime;
  isTimeFlexible: boolean;
  isExcluded: boolean;
  timeframes: PatientTimeFrames[];
  endTime?: LocalTime;
}

export interface FlexibleVisitDayCalculatedState {
  day: DayOfWeek;
  startDate: LocalDate;
  endDate: LocalDate;
  durationInHours: number | null;
  firstDuration: number;
  knownDurationInHours: number | null;
  isOvernight: boolean;
  isDayChecked: boolean;
  isDurationSelected: boolean;
  showTimesLabel: boolean;
  startsToday: boolean;
  mustChooseTimeslotInFuture: boolean;
  minStartTime: LocalTime;
  isValidRange: boolean;
  isSelectedTimeInAvailabilityRange: boolean;
}

export type FlexibleVisitDaysMap = HashMap<DayOfWeek, FlexibleVisitDayState>;
export type FlexibleVisitDaysCalculatedMap = Map<DayOfWeek, FlexibleVisitDayCalculatedState>;
export type FlexibleVisitProps = ReturnType<typeof useFlexibleVisit>;

function useFlexibleVisit(props: Params) {
  const hoursInWeek = getHoursInWeek(props.visit.publicVisitBroadcastDetails);
  const hoursPerDayMap = getHoursPerDayMap(props.visit.publicVisitBroadcastDetails);
  const daysInWeek = getDaysInWeek(props.visit.publicVisitBroadcastDetails);

  const [hoursInWeekLeft, setHoursInWeekLeft] = React.useState(hoursInWeek);
  const [daysInWeekLeft, setDaysInWeekLeft] = React.useState(daysInWeek);
  const [daysMap, setDaysMap] = React.useState<FlexibleVisitDaysMap>(getDaysMap(props.visit));

  const daysMapValues = [...daysMap.values()];
  const areAllDaysChecked = !daysMapValues.some((day) => day.isChecked === false);
  const areDaysSelectable = getAreDaysSelectable(props.visit.publicVisitBroadcastDetails);
  const areTimesFlexible = getAreTimesFlexible(props.visit.publicVisitBroadcastDetails);
  const isSelectedTimesInAvailabilityRange =
    getIsSelectedDaysTimesInAvailabilityRange(daysMapValues);

  const calculatedDaysMap = React.useMemo(
    () =>
      new Map(
        [...daysMap.entries()].map(([day, data]) => [
          day,
          getFlexibleVisitDayCalculatedState({
            day: day,
            state: data,
            daysMap: daysMap,
            visit: props.visit.publicVisitBroadcastDetails,
            areTimesFlexible: areTimesFlexible,
          }),
        ])
      ),
    [areTimesFlexible, daysMap, props.visit.publicVisitBroadcastDetails]
  );

  const days = React.useMemo(
    () =>
      [...daysMap.entries()]
        .filter(([_day, data]) => !data.isExcluded)
        .sort((day1, day2) => day1[0].ordinal() - day2[0].ordinal())
        .map(([day, data]) => ({
          state: data,
          calculated: assertDefined(calculatedDaysMap.get(day)),
        })),
    [calculatedDaysMap, daysMap]
  );

  const isValid = isFlexibleVisitValid(
    props.visit.publicVisitBroadcastDetails,
    days.map(({ calculated }) => calculated),
    daysMapValues,
    daysMap,
    hoursInWeekLeft,
    daysInWeekLeft
  );

  const selectDuration = (day: DayOfWeek, durationInHours: number) => {
    const data = assertDefined(daysMap.get(day));

    selectDurationWithShiftData(day, data, durationInHours);
  };

  const selectDurationWithShiftData = (
    day: DayOfWeek,
    data: FlexibleVisitDayState,
    durationInHours: number
  ) => {
    const duration = data.durations.find((d) => d.numberOfHours === durationInHours);
    const hasOnlyOneDuration = data.durations.length === 1 && duration?.isSelected === true;

    if (duration === undefined || hasOnlyOneDuration) {
      return;
    }

    if (duration.isSelected) {
      return updateState(
        daysMap.set(day, {
          ...data,
          durations: data.durations.map((d) => ({
            ...d,
            isSelected: false,
          })),
        })
      );
    }

    if (
      !canSelectDuration(
        duration,
        data.durations,
        daysMap,
        hoursInWeekLeft,
        daysInWeekLeft,
        hoursPerDayMap,
        areDaysSelectable,
        props.visit.publicVisitBroadcastDetails.allowMultipleCaregivers
      )
    ) {
      return;
    }

    const newStartTime = fixStartTimeOnDurationChange(data.startTime, durationInHours);

    updateState(
      daysMap.set(day, {
        ...data,
        startTime: newStartTime,
        endTime: newStartTime.plusMinutes(durationInHours * 60),
        durations: data.durations.map((d) => ({
          ...d,
          isSelected: d.numberOfHours === durationInHours && !d.isDisabled,
        })),
      })
    );
  };

  const toggleDay = (day: DayOfWeek) => {
    const data = assertDefined(daysMap.get(day));
    const isDayDisabled = getIsDayDisabled(
      daysMap,
      data,
      day,
      hoursInWeekLeft,
      daysInWeekLeft,
      areDaysSelectable,
      hoursPerDayMap,
      props.visit.publicVisitBroadcastDetails.allowMultipleCaregivers
    );

    const isDayChecked = data.isChecked === null ? null : !data.isChecked;

    if (isDayDisabled) {
      updateState(
        daysMap.set(day, {
          ...data,
          isChecked: data.isChecked === null ? null : false,
        })
      );
      return;
    }

    const selectedDuration = data.durations.find((d) => d.isSelected);

    const newData = {
      ...data,
      startTime: data.startTime,
      endTime:
        selectedDuration === undefined
          ? data.endTime
          : data.startTime.plusMinutes(selectedDuration.numberOfHours * 60),
      isChecked: isDayChecked,
    };

    updateState(daysMap.set(day, newData));

    if (isDayChecked === true && data.durations.length === 1) {
      selectDurationWithShiftData(day, newData, data.durations[0].numberOfHours);
    }
  };

  const toggleAllDays = () => {
    const newAreAllDaysChecked = !areAllDaysChecked;

    updateState(
      HashMap.fromArray(
        [...daysMap.entries()]
          .filter(([_day, data]) => !data.isExcluded)
          .map(([day, data]) => [
            day,
            {
              ...data,
              durations: data.durations.map((d) => ({
                ...d,
                isSelected: newAreAllDaysChecked ? data.durations.length === 1 : false,
              })),
              endTime:
                data.durations.length === 1 && data.durations[0].isSelected
                  ? data.startTime.plusMinutes(data.durations[0].numberOfHours * 60)
                  : data.endTime,
              isChecked: newAreAllDaysChecked,
            },
          ])
      )
    );
  };

  const updateTime = (day: DayOfWeek, value: number | number[] | null | undefined) => {
    if (value === undefined || value === null) {
      return;
    }

    const data = assertDefined(daysMap.get(day));

    const startTimeMinutes = localTimeToMinutes(data.startTime);
    const duration = data.durations.find((d) => d.isSelected)?.numberOfHours ?? 0;

    const { newStartTimeMinutes, newEndTimeMinutes } = Array.isArray(value)
      ? getStartAndEndTimeMinutes(value, duration)
      : { newStartTimeMinutes: value, newEndTimeMinutes: value + duration * 60 };

    const startTime =
      startTimeMinutes === newStartTimeMinutes
        ? data.startTime.minusMinutes(duration * 60 - (newEndTimeMinutes - newStartTimeMinutes))
        : data.startTime.plusMinutes(newStartTimeMinutes - startTimeMinutes);

    const endTime = startTime.plusMinutes(duration * 60);
    const isLiveIn = duration === 24;

    let newDaysMap = daysMap.set(day, {
      ...data,
      startTime,
      endTime,
    });

    if (isLiveIn) {
      newDaysMap = updateFlexibleDaysTimes({
        daysToUpdate: getDisabledFlexibleDays(daysMap),
        startTime: startTime,
        endTime: endTime,
        daysMap: newDaysMap,
      });
    }

    updateState(newDaysMap);
  };

  const updateState = (data: FlexibleVisitDaysMap) => {
    const newHoursInWeekLeft = calculateHoursLeftInWeek(
      props.visit.publicVisitBroadcastDetails,
      data
    );
    const newDaysInWeekLeft = calculateDaysLeftInWeek(
      props.visit.publicVisitBroadcastDetails,
      data
    );

    const newDaysMap = HashMap.fromArray(
      [...data.entries()].map(([day, entry]) => {
        const isDayDisabled = getIsDayDisabled(
          data,
          entry,
          day,
          newHoursInWeekLeft,
          newDaysInWeekLeft,
          areDaysSelectable,
          hoursPerDayMap,
          props.visit.publicVisitBroadcastDetails.allowMultipleCaregivers
        );

        return [
          day,
          {
            ...entry,
            isDisabled: isDayDisabled,
            isChecked: entry.isChecked === null ? null : entry.isChecked && !isDayDisabled,
            durations: entry.durations.map((d) => ({
              ...d,
              isDisabled: isDurationDisabled(
                d,
                entry.durations,
                data,
                newHoursInWeekLeft,
                newDaysInWeekLeft,
                hoursPerDayMap,
                areDaysSelectable,
                props.visit.publicVisitBroadcastDetails.allowMultipleCaregivers
              ),
            })),
          },
        ];
      })
    );

    setHoursInWeekLeft(newHoursInWeekLeft);
    setDaysInWeekLeft(newDaysInWeekLeft);
    setDaysMap(newDaysMap);
  };

  return {
    days: days,
    daysMap: daysMap,
    areAllDaysChecked: areAllDaysChecked,
    isSelectedTimesInAvailabilityRange: isSelectedTimesInAvailabilityRange,
    hoursInWeekLeft: hoursInWeekLeft,
    daysInWeekLeft: daysInWeekLeft,
    areTimesFlexible: areTimesFlexible,
    areDaysSelectable: areDaysSelectable,
    isValid: isValid,
    onToggleDay: toggleDay,
    onToggleAllDays: toggleAllDays,
    onSelectDuration: selectDuration,
    onUpdateTime: updateTime,
  };
}

export default useFlexibleVisit;
